import MonkeyPatch from "./monkey_patch";

/**
 * Returns true iff `el` is a media (audio or video) element;
 */
const isMediaElement = (el) => el.tagName === "VIDEO" || el.tagName === "AUDIO";

/**
 * Assuming `el` is a playing video, returns `true` iff it has audio data.
 */
const doesVideoHaveAudio = (el) => {
  return (
    (el.audioTracks != null && el.audioTracks.length > 0) ||
    (el.webkitAudioDecodedByteCount != null &&
      el.webkitAudioDecodedByteCount > 0)
  );
};

// Minimum bin size of a web audio analyzer node frequency spectrum. See:
// https://developer.mozilla.org/en-US/docs/Web/API/AnalyserNode/fftSize#Value.
const MIN_FFT_SIZE = 32;

/**
 * Global controller for web audio. This works by monkey-patching the
 * `AudioContext` constructor and redefining `AudioContext.destination`
 * (normally an `AudioDestinationNode` that routes sound to the speakers)
 * to a node that allows the global controller to intercept and control all
 * output. This is used by `GlobalAudioController`--it's not intended to be
 * used directly. `cb` is a function that will be called the first time any
 * `AudioContext` produces a sound.
 */
class GlobalWebAudioController {
  // if process.env.NODE_ENV === "test"
  /**
   * Resets all state back to it's uninitialized state.
   * Used for testing purposes.
   */
  static reset() {
    GlobalWebAudioController._firstSoundMade = false;
    GlobalWebAudioController._muted = null;
  }
  // #endif

  static init(cb) {
    GlobalWebAudioController._muted = null;
    GlobalWebAudioController._cb = cb;
    GlobalWebAudioController._firstSoundMade = false;
    GlobalWebAudioController._gainNodes = [];

    try {
      const originalAudioContext =
        window.AudioContext || window.webkitAudioContext;

      /**
       * An alternative to monkey-patching the `AudioContext` constructor would
       * be to monkey-patch the `AudioContext.prototype.destination` getter,
       * but this doesn't work on old (pre-getter) browsers.
       */
      window.AudioContext = window.webkitAudioContext = function AudioContext(
        ...args
      ) {
        const audioCtx = new originalAudioContext(...args);
        const gainNode = audioCtx.createGain();
        const analyserNode = audioCtx.createAnalyser();
        // We just want to know if the `AudioContext` is producing _any_ sound
        // and don't care about the frequency spectrum per se. We therefore used
        // the smallest allowed FFT size.
        analyserNode.fftSize = MIN_FFT_SIZE;
        const freqData = new Uint8Array(analyserNode.frequencyBinCount);
        const soundPollInterval = setInterval(() => {
          analyserNode.getByteFrequencyData(freqData);
          let i = 0;
          for (; i < freqData.length; i++) {
            if (freqData[i] !== 0) break;
          }
          if (i === freqData.length) return;
          clearInterval(soundPollInterval);
          GlobalWebAudioController._handleFirstSound();
        }, 10);
        /**
         * The resulting node structure is:
         *   input -> analyser -> gain -> destination
         * We use the analyser node to detect when the AudioContext is producing
         * sound and the gain node to globally control output.
         */
        const originalDestination = audioCtx.destination;
        analyserNode.connect(gainNode);
        gainNode.connect(originalDestination);
        gainNode.gain.value = GlobalWebAudioController._muted ? 0 : 1;
        GlobalWebAudioController._gainNodes.push(gainNode);

        Object.defineProperty(audioCtx, "destination", {
          value: analyserNode,
          // Ensure that our destination node is the only destination node.
          writable: false,
          configurable: false,
        });

        return audioCtx;
      };
    } catch (_) {
      console.error("Unable to monkey-patch AudioContext");
    }
  }

  static _handleFirstSound() {
    if (GlobalWebAudioController._firstSoundMade) return;
    GlobalWebAudioController._cb();
    GlobalWebAudioController._firstSoundMade = true;
  }

  /**
   * Mutes all `AudioContext`s when `shouldMute === true` and restores all
   * `AudioContext`s to the state set by the user when `shouldMute === false`.
   */
  static setMuted(shouldMute) {
    if (GlobalWebAudioController._muted === shouldMute) {
      return;
    }
    GlobalWebAudioController._muted = shouldMute;
    GlobalWebAudioController._gainNodes.forEach((gainNode) => {
      gainNode.gain.value = shouldMute ? 0 : 1;
    });
  }
}

/**
 * Utility for tracking media elements. Media elements can come into existence
 * in several different ways, which requies multiple different tricks for
 * becoming aware of their existence:
 *   1) They may exist in the DOM on page load (i.e. be embedded directly in
 *      the HTML document) - these can be detected by querying the dom for video
 *      and audio elements
 *   2) They can be produced from an HTML string (e.g. `someEl.innerHTML =
 *      "<audio ... />"`) and appended to the DOM. They must fire a "loadstart"
 *      event after being appended to the DOM, or the `load` or `play` methods
 *      may be called directly on the element. These can be detected by
 *      listening for "loadstart" events and by monkey-patching the `load` and
 *      `play` methods. This is true regardless of whether or not autoplay is
 *      true.
 *   3) `document.createElement` - these are detected by monkey-patching
 *      `Document.prototype.createElement`. These won't be caught by the
 *      approaches in 2 if the "autoplay" attribute is set to `true` (since they
 *      can make sound without ever being explicitly loaded, played, or append
 *      to the DOM).
 *   4) The `Audio` constructor - these are detected by monkey-patching the
 *      `Audio` constructor.
 */
class MediaElementWatcher {
  constructor() {
    throw new Error("Cannot construct instances of MediaElementWatcher.");
  }

  /**
   * Initializes the class, and immediately calls function `cb` once for each
   * media element that already exists. Subsequently calls `cb` whenever a new
   * media element is created. This should only be called once.
   */
  static init(cb) {
    MediaElementWatcher._cb = cb;
    MediaElementWatcher._seenElements = new WeakSet();

    const originalCreateElement = Document.prototype.createElement;
    MonkeyPatch.patchFunction(
      Document.prototype,
      "createElement",
      function (...args) {
        const el = originalCreateElement.apply(this, args);
        MediaElementWatcher._handleNewElement(el);
        return el;
      }
    );

    const originalAudio = window.Audio;
    window.Audio = function Audio(...args) {
      const a = new originalAudio(...args);
      MediaElementWatcher._handleNewElement(a);
      return a;
    };
    window.Audio.prototype = originalAudio.prototype;

    ["play", "load"].forEach((methodName) => {
      const originalMethod = HTMLMediaElement.prototype[methodName];
      MonkeyPatch.patchFunction(
        HTMLMediaElement.prototype,
        methodName,
        function (...args) {
          MediaElementWatcher._handleNewElement(this);
          return originalMethod.apply(this, args);
        }
      );
    });

    window.addEventListener(
      "loadstart",
      (e) => {
        MediaElementWatcher._handleNewElement(e.target);
      },
      true
    );

    document.querySelectorAll("video, audio").forEach((el) => {
      MediaElementWatcher._cb(el);
    });
  }

  static _handleNewElement(el) {
    if (!isMediaElement(el) || MediaElementWatcher._seenElements.has(el)) {
      return;
    }
    MediaElementWatcher._seenElements.add(el);
    MediaElementWatcher._cb(el);
  }
}

/**
 * Global controller for media (audio and video) elements. Used by
 * `GlobalAudioController` and not intended to be used directly. `cb` is a
 * function that will be called the first time any media element can
 * produce a sound (were global control not applied).
 */
class GlobalMediaElementAudioController {
  // #if process.env.NODE_ENV === "test"
//   /**
//    * Resets all state back to it's uninitialized state.
//    * Used for testing purposes.
//    */
//   static reset() {
//     GlobalMediaElementAudioController._firstSoundMade = false;
//     GlobalMediaElementAudioController._globallyMuted = null;
//   }
  // #endif

  constructor() {
    throw new Error(
      "Cannot construct instances of GlobalMediaElementAudioController."
    );
  }

  static init(cb) {
    GlobalMediaElementAudioController._globallyMuted = null;
    /**
     * We keep track of the values for el.muted set by the user for each media
     * el. When we globally unmute, we restore the set state (so calling
     * `GlobalMediaElementAudioController.unmute()` does not unmute
     * elements that the user has set to be muted).
     */
    GlobalMediaElementAudioController._elToSetState = new WeakMap();
    GlobalMediaElementAudioController._allMediaEls = [];
    GlobalMediaElementAudioController._firstSoundMade = false;
    GlobalMediaElementAudioController._cb = cb;

    try {
      GlobalMediaElementAudioController._originalMutedSetter =
        Object.getOwnPropertyDescriptor(
          HTMLMediaElement.prototype,
          "muted"
        ).set;
      const originalMutedGetter = Object.getOwnPropertyDescriptor(
        HTMLMediaElement.prototype,
        "muted"
      ).get;

      MonkeyPatch.patchSetter(
        HTMLMediaElement.prototype,
        "muted",
        function (val) {
          GlobalMediaElementAudioController._elToSetState.set(this, val);
          if (GlobalMediaElementAudioController._canMakeSound(this)) {
            GlobalMediaElementAudioController._handleFirstSound();
          }

          const systemMutedState = originalMutedGetter.call(this);
          if (
            GlobalMediaElementAudioController._globallyMuted &&
            (systemMutedState || !val)
          ) {
            return;
          }
          GlobalMediaElementAudioController._originalMutedSetter.call(
            this,
            val
          );
        }
      );

      // Patch the "muted" getter so that it returns the value set by the user,
      // rather than the underlying "system" value.
      MonkeyPatch.patchGetter(HTMLMediaElement.prototype, "muted", function () {
        const recordedSetState =
          GlobalMediaElementAudioController._elToSetState.get(this);
        return recordedSetState == null
          ? originalMutedGetter.call(this)
          : recordedSetState;
      });
      // #if process.env.NODE_ENV === "test"
//       // Expose the underlying system muted setting to tests.
//       Object.defineProperty(HTMLMediaElement.prototype, "systemMutedState", {
//         get: originalMutedGetter,
//       });
      // #endif
    } catch (_) {
      /**
       * Monkey-patching will fail on old (pre-setter) browsers. In this case,
       * we have no way of keeping track of the set state.
       * (Listening for "volumechange" events doesn't work, because if we force
       * the state to `true` due to globally muting, then the event will not
       * fire if the user subsequently sets the `muted` to `true`.) In this
       * case, we just keep the page globally muted.
       */
      GlobalMediaElementAudioController._originalMutedSetter = function (mute) {
        this.muted = mute;
      };
      console.error("Unable to monkey-patch HTMLMediaElement.muted");
    }

    MediaElementWatcher.init((el) => {
      if (!GlobalMediaElementAudioController._allMediaEls.includes(el)) {
        GlobalMediaElementAudioController._allMediaEls.push(el);
      }
      GlobalMediaElementAudioController._watchForSound(el);
      GlobalMediaElementAudioController._toggleElementMuted(
        el,
        GlobalMediaElementAudioController._globallyMuted
      );
    });
  }

  /**
   * Returns `true` iff `el` would be able to make a sound were it not being
   * controlled by this global controller. We consider an element able to make
   * a sound if it is either an audio element or is a video with audio tracks.
   * Assumes that `el` is in the playing state (whether or not a video has
   * audio cannot be determined for all browsers for media that are not
   * playing).
   */
  static _canMakeSound(el) {
    const isMuted = GlobalMediaElementAudioController._elToSetState.get(el);
    return !isMuted && (el.tagName === "AUDIO" || doesVideoHaveAudio(el));
  }

  static _watchForSound(el) {
    if (!el.paused && GlobalMediaElementAudioController._canMakeSound(el)) {
      GlobalMediaElementAudioController._handleFirstSound();
      return;
    }

    el.addEventListener("playing", () => {
      if (!GlobalMediaElementAudioController._canMakeSound(el)) return;
      GlobalMediaElementAudioController._handleFirstSound();
    });
  }

  /**
   * Forces `el.muted` to `muted` (a boolean). Assumes that if `muted === true`,
   * the value of `el.muted` is the state set by the user.
   * (i.e. This is not idempotent.)
   */
  static _toggleElementMuted(el, muted) {
    if (muted) {
      GlobalMediaElementAudioController._elToSetState.set(el, el.muted);
      GlobalMediaElementAudioController._originalMutedSetter.call(el, true);
      return;
    }

    GlobalMediaElementAudioController._originalMutedSetter.call(
      el,
      GlobalMediaElementAudioController._elToSetState.get(el)
    );
  }

  static _toggleMediaMuted(muted) {
    GlobalMediaElementAudioController._allMediaEls.forEach((el) => {
      GlobalMediaElementAudioController._toggleElementMuted(el, muted);
    });
  }

  static _handleFirstSound() {
    if (GlobalMediaElementAudioController._firstSoundMade) return;
    GlobalMediaElementAudioController._cb();
    GlobalMediaElementAudioController._firstSoundMade = true;
  }

  /**
   * Mutes all HTML media elements when `shouldMute === true` and restores the
   * set state of all HTML media elements when `shouldMute === false`.
   */
  static setMuted(shouldMute) {
    if (GlobalMediaElementAudioController._globallyMuted === shouldMute) {
      return;
    }
    GlobalMediaElementAudioController._toggleMediaMuted(shouldMute);
    GlobalMediaElementAudioController._globallyMuted = shouldMute;
  }
}

/**
 * Global controller for all possible sources of audio in a web page, except
 * for `iframe`s. The public interface consists of only two methods:
 *   - `GlobalAudioController.setMuted` - takes a boolean indicating whether
 *      audio from all sound sources (HTML media elements and web audio) should
 *      be muted (true) or not (false);
 *   - `GlobalAudioController.onFirstSound` - receives one argument, a function
 *      that will be called immediately if the page is already making sound or
 *      otherwise will be called once when the page first makes a sound
 *      (possibly never).
 */
class GlobalAudioController {
  // #if process.env.NODE_ENV === "test"
//   /**
//    * Just to facilitate testing of `onFirstSound` by allowing the registered
//    * callback to be run multiple times when new sources of sound are added to
//    * the testing environment page.
//    */
//   static reset() {
//     GlobalAudioController._firstSoundMade = false;
//     GlobalAudioController._onFirstSoundCallbacks = [];
// 
//     GlobalMediaElementAudioController.reset();
//     GlobalWebAudioController.reset();
//   }
  // #endif

  constructor() {
    throw new Error("Cannot construct instances of GlobalAudioController.");
  }

  /**
   * This class is invasive insofar as implements a series of monkey patches on
   * browser API objects. We thus only initialize it when it's actually used.
   */
  static _init() {
    if (GlobalAudioController._initialized) {
      return;
    }
    GlobalAudioController._initialized = true;
    GlobalAudioController._firstSoundMade = false;
    GlobalAudioController._onFirstSoundCallbacks = [];

    const handleFirstSound = () => {
      if (GlobalAudioController._firstSoundMade) {
        return;
      }
      GlobalAudioController._firstSoundMade = true;
      GlobalAudioController._onFirstSoundCallbacks.forEach((cb) => cb());
    };
    GlobalMediaElementAudioController.init(handleFirstSound);
    GlobalWebAudioController.init(handleFirstSound);
  }

  static setMuted(shouldMute) {
    GlobalAudioController._init();
    GlobalMediaElementAudioController.setMuted(shouldMute);
    GlobalWebAudioController.setMuted(shouldMute);
  }

  static runAfterFirstSound(cb) {
    GlobalAudioController._init();
    if (GlobalAudioController._firstSoundMade) {
      cb();
      return;
    }

    GlobalAudioController._onFirstSoundCallbacks.push(cb);
  }
}
GlobalAudioController._initialized = false;

export default GlobalAudioController;



// WEBPACK FOOTER //
// ./src/js/lib/global_audio_controller.js